import { useEffect, useRef, type RefObject } from "react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useInteractiveElementState } from "@/hooks/useInteractiveElementState";
import {
  InteractiveElementStatusKind,
  type InteractiveElementScore,
} from "@/lib/interactiveElementState";
import { reportLearningProgress } from "@/lib/reportLearningProgress";

interface ReportInteractiveLearningProgressOptions {
  /** When false, the hook does nothing. Defaults to false. */
  enabled?: boolean;
  /**
   * When true, posts `in_progress` on the first forward transition as well as
   * `completed`. Reflections leave this off and post completed only.
   */
  includeInProgress?: boolean;
  /**
   * A status the server already recorded. Copying it onto a not-started
   * element is not new progress. A later kind still posts.
   */
  alreadyReportedKind?: InteractiveElementStatusKind;
}

/**
 * Not started never counts. In progress counts only when the caller asked
 * (quizzes). Completed always counts.
 */
const isStatusExcludedFromProgress = (
  kind: InteractiveElementStatusKind,
  includeInProgress: boolean
): boolean => {
  if (kind === InteractiveElementStatusKind.NotStarted) return true;
  if (kind === InteractiveElementStatusKind.InProgress && !includeInProgress) {
    return true;
  }
  return false;
};

/**
 * True when this status is not a new transition. A new status is recorded even
 * when it is excluded, so a later completed is not treated as a remount.
 */
const isStatusNotNew = (
  kind: InteractiveElementStatusKind,
  lastSeenStatusRef: RefObject<InteractiveElementStatusKind>
): boolean => {
  if (kind === lastSeenStatusRef.current) return true;
  lastSeenStatusRef.current = kind;
  return false;
};

/**
 * Posts LearningProgress when an interactive element's local status moves
 * forward — used by quizzes (in-progress then completed) and reflections
 * (completed after the last answer is evaluated).
 */
export const useReportInteractiveLearningProgress = (
  contentElementId: string,
  {
    enabled = false,
    includeInProgress = false,
    alreadyReportedKind,
  }: ReportInteractiveLearningProgressOptions = {}
): void => {
  const { enrollmentId, trainerMode, refreshProgress } = useCourseDetailStore();
  const { status } = useInteractiveElementState(contentElementId, enrollmentId);
  // Mount-time status so an already-finished element is not re-posted on visit.
  const lastSeenStatusRef = useRef(status.kind);

  const reportWhenLocalStatusMovesForward = () => {
    const isReportProgressDeactivated =
      !enabled || !enrollmentId || trainerMode;
    const enrollmentIdToRecord = isReportProgressDeactivated
      ? null
      : enrollmentId;
    const kind = status.kind;
    const previousKind = lastSeenStatusRef.current;
    const copiedServerStatusOntoAFreshElement =
      alreadyReportedKind === kind &&
      previousKind === InteractiveElementStatusKind.NotStarted;

    if (
      !enrollmentIdToRecord ||
      isStatusNotNew(kind, lastSeenStatusRef) ||
      isStatusExcludedFromProgress(kind, includeInProgress) ||
      copiedServerStatusOntoAFreshElement
    ) {
      return;
    }

    const score: InteractiveElementScore | undefined =
      kind === InteractiveElementStatusKind.Completed
        ? status.score
        : undefined;

    reportLearningProgress(
      enrollmentIdToRecord,
      contentElementId,
      kind,
      score,
      refreshProgress
    );
  };

  useEffect(reportWhenLocalStatusMovesForward, [
    enabled,
    includeInProgress,
    alreadyReportedKind,
    enrollmentId,
    trainerMode,
    refreshProgress,
    contentElementId,
    status,
  ]);
};
